import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { PURCHASE_COLUMNS, SALE_COLUMNS } from '../../utils/documentColumns';
import { useSettings, type SettingSpec } from '../../hooks/useSettings';
import { usePermission } from '../../hooks/usePermission';
import { useKeyboardForm } from '../../hooks/useKeyboardForm';
import { useToast } from '../../components/ui/Toast';
import { ErrorNotice, Loading, PageHeader } from '../../components/ui';
import { BusinessDetails } from './BusinessDetails';
import { Field, NumberInput, Select } from '../../components/ui/Form';
import { ImagePicker } from '../../components/ui/ImagePicker';
import { usePrintHeader } from '../../hooks/usePrintHeader';

/**
 * How the shop runs.
 *
 * The list is built from what the server sends, so a switch cannot appear here
 * that nothing honours — and every one of these says, in plain words, where its
 * effect shows up. A settings screen whose changes do nothing visible teaches
 * people to stop trusting the ones that do.
 */
export function SettingsPage() {
  const { can } = usePermission();
  const client = useQueryClient();
  const onEnter = useKeyboardForm();
  const toast = useToast();
  const { catalogue, values, isLoading } = useSettings();
  const { header } = usePrintHeader();
  const [logo, setLogo] = useState<string | null | undefined>(undefined);
  const [error, setError] = useState<unknown>(null);

  const saveLogo = useMutation({
    mutationFn: (dataUri: string | null) => api.put('/company/logo', { logoDataUri: dataUri }),
    onSuccess: () => {
      toast('Logo updated', 'It will show on printed bills');
      void client.invalidateQueries({ queryKey: ['companies'] });
    },
    onError: setError,
  });

  const [draft, setDraft] = useState<Record<string, string>>({});

  useEffect(() => setDraft(values), [values]);

  const save = useMutation({
    mutationFn: () => api.put('/settings', draft),
    onSuccess: () => {
      toast('Settings saved', 'Documents and alerts will use these from now on');
      setError(null);
      void client.invalidateQueries({ queryKey: ['settings'] });
      void client.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: setError,
  });

  if (isLoading) return <Loading what="Loading settings" />;

  const groups = [...new Set(catalogue.map((s) => s.group))];
  const changed = catalogue.filter((s) => draft[s.key] !== values[s.key]).length;
  const editable = can('settings.manage');

  const control = (spec: SettingSpec): JSX.Element => {
    const value = draft[spec.key] ?? spec.default;
    const set = (v: string): void => setDraft({ ...draft, [spec.key]: v });

    if (spec.type === 'NUMBER') {
      return (
        <NumberInput
          value={value}
          onChange={set}
          decimals={0}
          max={spec.max}
          suffix={spec.key.endsWith('_days') ? 'days' : undefined}
          disabled={!editable}
        />
      );
    }
    if (spec.type === 'COLUMNS') {
      // Ticking a column decides what the cashier sees and can change while
      // billing; the engine works every figure out either way.
      const all = spec.key === 'documents.sale_columns' ? SALE_COLUMNS : PURCHASE_COLUMNS;
      const on = new Set(value.split(',').map((k) => k.trim()).filter(Boolean));
      const toggle = (key: string): void => {
        if (on.has(key)) on.delete(key);
        else on.add(key);
        set(all.filter((c) => on.has(c.key)).map((c) => c.key).join(','));
      };

      return (
        <div className="grid gap-1.5 sm:grid-cols-2">
          {all.map((c) => (
            <label
              key={c.key}
              className={`flex items-start gap-2 rounded-md border px-2.5 py-2 text-sm ${
                c.fixed ? 'border-silver-100 bg-silver-50' : on.has(c.key) ? 'border-gold-400 bg-gold-50' : 'border-silver-100'
              }`}
            >
              <input
                type="checkbox"
                className="mt-0.5"
                checked={c.fixed || on.has(c.key)}
                disabled={!editable || c.fixed}
                onChange={() => toggle(c.key)}
              />
              <span>
                <span className="font-medium">
                  {c.label}
                  {c.fixed ? <span className="ml-1 text-2xs font-normal text-ink-faint">always shown</span> : null}
                  {c.editable ? <span className="ml-1 text-2xs font-normal text-gold-700">editable</span> : null}
                </span>
                <span className="block text-2xs text-ink-soft">{c.about}</span>
              </span>
            </label>
          ))}
        </div>
      );
    }
    if (spec.type === 'CHOICE') {
      return (
        <Select
          value={value}
          onChange={set}
          disabled={!editable}
          options={(spec.choices ?? []).map((c) => ({ value: c, label: c.toLowerCase() }))}
        />
      );
    }
    // Long text is what gets printed on paper, so it is shown at the size it
    // will be read.
    return (
      <textarea
        className="field h-24"
        value={value}
        maxLength={spec.maxLength}
        disabled={!editable}
        onChange={(e) => set(e.target.value)}
      />
    );
  };

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    save.mutate();
  };

  return (
    <>
      <PageHeader
        title="Settings"
        subtitle="Everything here changes something you can see — each one says where."
        actions={
          editable ? (
            <button className="btn-primary" disabled={changed === 0 || save.isPending} onClick={submit}>
              {save.isPending ? 'Saving…' : changed === 0 ? 'Nothing changed' : `Save ${changed} change${changed === 1 ? '' : 's'}`}
            </button>
          ) : null
        }
      />
      <ErrorNotice error={error} />

      {!editable ? (
        <p className="mb-4 rounded-md border border-silver-100 bg-surface px-3 py-2 text-sm text-ink-soft">
          You can see how the shop is set up, but changing it is the owner’s to do.
        </p>
      ) : null}

      <div className="max-w-2xl">
        <BusinessDetails editable={editable} />
      </div>

      <form autoComplete="off" onKeyDown={onEnter} onSubmit={submit} className="mt-6 max-w-2xl space-y-6">
        {editable ? (
          <section>
            <h2 className="mb-2 text-sm font-semibold">Shop logo</h2>
            <div className="rounded-md border border-silver-100 bg-surface p-4 shadow-card">
              <Field label="Logo" hint="Printed at the top of every bill.">
                <ImagePicker
                  value={logo === undefined ? header.logoDataUri ?? null : logo}
                  onChange={(uri) => {
                    setLogo(uri);
                    saveLogo.mutate(uri);
                  }}
                />
              </Field>
            </div>
          </section>
        ) : null}

        {groups.map((group) => (
          <section key={group}>
            <h2 className="mb-2 text-sm font-semibold">{group}</h2>
            <div className="space-y-4 rounded-md border border-silver-100 bg-surface p-4 shadow-card">
              {catalogue
                .filter((s) => s.group === group)
                .map((spec) => (
                  <Field key={spec.key} label={spec.label} hint={spec.usedFor}>
                    {control(spec)}
                  </Field>
                ))}
            </div>
          </section>
        ))}
      </form>
    </>
  );
}
